import { useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const mb = (n) => (n < 1048576 ? `${Math.max(1, Math.round((n || 0) / 1024))} KB` : `${(n / 1048576).toFixed(1).replace(".", ",")} MB`);
const seg = "flex-1 h-11 text-[15px] font-semibold rounded-[12px] transition-colors";

export function PrepReportDialog({ pid, s, open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState(s.owner.phone || !s.owner.email ? "whatsapp" : "email");
  const [phone, setPhone] = useState(s.owner.phone || "");
  const [email, setEmail] = useState(s.owner.email || "");
  const to = s.owner.call ? `${/^(Sra|Dra|Eng\.ª)/.test(s.owner.call) ? "à" : "ao"} ${s.owner.call}` : "ao proprietário";
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/processes/${pid}/prep/relatorio/upload`, await makeForm([["file", f]]));
      setFile(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/processes/${pid}/prep/relatorio/send`, { file_id: file.id, channel, phone, email });
      if (data.wa_url) window.open(data.wa_url, "_blank");
      toast.success(channel === "email" ? "Relatório enviado por email" : "Mensagem pronta no WhatsApp");
      onDone();
      onClose();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="prep-report-dialog">
        <DialogHeader><DialogTitle className="font-heading text-ec-green">Relatório de avaliação</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="block rounded-[14px] border-2 border-dashed border-slate-200 px-4 py-4 cursor-pointer hover:bg-slate-50" data-testid="prep-report-drop">
            <input type="file" accept="application/pdf,.pdf" className="sr-only" onChange={(e) => pick(e.target.files?.[0])} data-testid="prep-report-file" />
            {file ? (
              <span className="flex items-center gap-3"><FileText className="text-red-500 shrink-0" size={28} />
                <span className="min-w-0"><span className="block font-semibold text-slate-900 truncate" data-testid="prep-report-filename">{file.filename}</span>
                  <span className="block text-sm text-slate-500">{mb(file.size)} · carregado agora</span></span></span>
            ) : <span className="block text-center text-[15px] text-slate-600">{busy ? "A carregar…" : "Escolher o PDF do relatório"}</span>}
          </label>
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-1.5">Enviar por</div>
            <div className="flex gap-1 p-1 rounded-[14px] border border-slate-200">
              {[["whatsapp", "WhatsApp"], ["email", "Email"]].map(([k, l]) => (
                <button key={k} onClick={() => setChannel(k)} className={`${seg} ${channel === k ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`prep-report-channel-${k}`}>{l}</button>
              ))}
            </div>
            {channel === "whatsapp"
              ? <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telemóvel do proprietário" className="mt-2 w-full h-11 px-3 rounded-[12px] border border-slate-200" data-testid="prep-report-phone" />
              : <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email do proprietário" className="mt-2 w-full h-11 px-3 rounded-[12px] border border-slate-200" data-testid="prep-report-email" />}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-1.5">Mensagem</div>
            <p className="rounded-[14px] bg-lime-50 border border-lime-100 px-4 py-3 text-[15px] text-slate-800 whitespace-pre-line" data-testid="prep-report-message">{s.report_preview}</p>
          </div>
          <button disabled={!file || busy} onClick={send} className="w-full h-12 rounded-[14px] bg-emerald-500 text-white text-[16px] font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors" data-testid="prep-report-send">
            Enviar {to}
          </button>
          <p className="text-center text-sm text-slate-500">Vai saber quando o abrir · o link expira em 60 dias</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
